import React from "react";
import { HelpCircle, Mail, Layers, FileWarning, Lightbulb, Network, Fingerprint, History, FileText } from "lucide-react";

/**
 * StrategicQuestion — bloc d'interpellation B2B (V3 "ultra stratégique").
 * Réutilisé en haut des pages /mssante-assureurs et /lutte-fraude-sante,
 * juste après le hero. Pose les 4 questions pivot sur la maîtrise du circuit
 * de transmission documentaire.
 */
export default function StrategicQuestion() {
  return (
    <section className="relative py-20 lg:py-24 overflow-hidden bg-[var(--mc-navy-900)]" data-testid="section-strategic-question">
      <div className="absolute inset-0 pointer-events-none opacity-50">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] rounded-full"
             style={{ background: "radial-gradient(circle, rgba(45,123,255,0.10), transparent 60%)" }} />
      </div>
      <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#0284C7]">
          <HelpCircle size={13} /> Une question simple
        </div>

        <h2 className="mt-4 font-display text-white text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight max-w-4xl">
          Comment vos équipes réceptionnent-elles aujourd'hui les{" "}
          <span className="text-[#0284C7]">pièces justificatives</span> des professionnels de santé&nbsp;?
        </h2>

        <p className="mt-6 text-[15px] text-[var(--mc-muted)] leading-relaxed max-w-3xl">
          Entre emails, plateformes multiples et circuits non standardisés, les flux documentaires
          sont souvent :
        </p>
        <div className="mt-5 grid sm:grid-cols-3 gap-3 max-w-3xl">
          <Pain icon={<Layers size={16} />} text="Peu homogènes" />
          <Pain icon={<History size={16} />} text="Difficilement traçables" />
          <Pain icon={<FileWarning size={16} />} text="Insuffisamment sécurisés" />
        </div>

        <div className="mt-10 pt-8 border-t border-[rgba(127,179,255,0.14)]">
          <p className="text-[15px] lg:text-[16px] text-white/95 leading-relaxed max-w-3xl">
            La vraie question n'est plus uniquement la <em>réception</em>. C'est la{" "}
            <strong className="text-[var(--mc-mint)]">maîtrise du circuit de transmission</strong>.
          </p>
          <p className="mt-3 text-[14px] text-[var(--mc-muted)]">Disposez-vous aujourd'hui :</p>
          <div className="mt-5 grid md:grid-cols-2 gap-3 max-w-4xl">
            <Pivot icon={<Mail size={16} />} text="D'un canal unique et sécurisé ?" />
            <Pivot icon={<Fingerprint size={16} />} text="D'une identification fiable des émetteurs ?" />
            <Pivot icon={<Network size={16} />} text="D'une traçabilité complète des échanges ?" />
            <Pivot icon={<FileText size={16} />} text="D'un cadre simple, clair et opposable pour structurer ces flux ?" />
          </div>
        </div>

        <div className="mt-10 inline-flex items-start gap-3 rounded-lg border border-[rgba(110,231,197,0.30)] bg-[rgba(110,231,197,0.06)] p-4 lg:p-5 max-w-3xl">
          <Lightbulb size={18} className="text-[var(--mc-mint)] shrink-0 mt-0.5" />
          <p className="text-[13.5px] text-white/90 leading-relaxed">
            Sans cela, les échanges deviennent un{" "}
            <strong className="text-[var(--mc-mint)]">point de fragilité dans la chaîne de confiance</strong>.
          </p>
        </div>
      </div>
    </section>
  );
}

function Pain({ icon, text }) {
  return (
    <div className="rounded-lg border border-[rgba(255,170,80,0.28)] bg-[rgba(255,170,80,0.04)] p-3 flex items-center gap-2.5">
      <span className="text-[#B45309] shrink-0">{icon}</span>
      <span className="text-[13.5px] text-white/90">{text}</span>
    </div>
  );
}

function Pivot({ icon, text }) {
  return (
    <div className="rounded-lg border border-[rgba(127,179,255,0.18)] bg-[rgba(45,123,255,0.04)] p-4 flex items-start gap-3">
      <span className="grid place-items-center h-8 w-8 rounded-md bg-[rgba(45,123,255,0.12)] border border-[rgba(127,179,255,0.28)] text-[#0284C7] shrink-0">
        {icon}
      </span>
      <span className="text-[14px] text-white/95 leading-snug pt-1">{text}</span>
    </div>
  );
}
